import {
  Badge,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import { MenuRelationDetail } from '@/pages/externals/pos-menu-masters/components/MenuRelationDetail';
import {
  categoryLabel,
  displayPrice,
  formatYen,
  hasChildren,
  MENU_STATUS_STYLE,
  MenuCategory,
  menuCategory,
  MenuStatus,
  menuStatus,
  menuStatusLabel,
  salesPeriod,
} from '@/pages/externals/pos-menu-masters/service';
import { MosPosMenu } from '@/services/MosPosMenuMaster';

type Props = {
  menus: MosPosMenu[];
  businessDate: string;
};

/** 一覧の区分は SCR009 の teal / amber バッジで見分けられるようにします。 */
const categoryBadgeStyle = (category: MenuCategory) =>
  category === 'set'
    ? { bg: '#FEEBC8', borderColor: '#FBD38D', color: '#7B341E' }
    : { bg: '#E6FFFA', borderColor: '#B2F5EA', color: '#234E52' };

const tableBadgeStyle: React.CSSProperties = {
  padding: '2px 8px',
};

/** 状態バッジ。配色はサマリカードと共有し、同じ状態が別の色に見えないようにします。 */
const statusBadgeStyle = (status: MenuStatus): React.CSSProperties => ({
  ...tableBadgeStyle,
  ...MENU_STATUS_STYLE[status],
  borderWidth: '1px',
  borderStyle: 'solid',
});

/** SCR009 の一覧ヘッダーに合わせる共通セルスタイルです。 */
const tableHeaderCellStyle: React.CSSProperties = {
  backgroundColor: '#F5F8FB',
  borderBottom: '1px solid #E2E8F0',
  color: '#718096',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  padding: '12px 14px',
  position: 'sticky',
  textTransform: 'uppercase',
  top: 0,
  zIndex: 1,
};

/**
 * SCR009 No.14 のメニュー一覧。状態は API の business_date を基準に判定します。
 * 行をクリックすると子商品を展開します（No.15）。
 */
export const MenuMasterTable: React.FC<Props> = ({ menus, businessDate }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <Box
      data-testid="list-table-wrap"
      style={{
        border: '1px solid #E2E8F0',
        borderRadius: '5px',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      <Table
        size="sm"
        bg="white"
        style={{ minWidth: '1000px' }}
        data-testid="list-table"
      >
        <Thead
          style={{ backgroundColor: '#F5F8FB', border: '1px solid #E2E8F0' }}
          data-testid="list-table-head"
        >
          <Tr>
            <Th style={{ ...tableHeaderCellStyle, width: '38px' }} />
            <Th
              style={{ ...tableHeaderCellStyle, width: '140px' }}
              data-testid="list-table-header-code"
            >
              POSメニューコード
            </Th>
            <Th style={{ ...tableHeaderCellStyle, width: '150px' }}>
              WEBメニューコード
            </Th>
            <Th style={tableHeaderCellStyle}>商品名</Th>
            <Th style={{ ...tableHeaderCellStyle, width: '74px' }}>区分</Th>
            <Th isNumeric style={{ ...tableHeaderCellStyle, width: '90px' }}>
              販売価格
            </Th>
            <Th style={{ ...tableHeaderCellStyle, width: '180px' }}>
              販売期間
            </Th>
            <Th style={{ ...tableHeaderCellStyle, width: '88px' }}>状態</Th>
            <Th isNumeric style={{ ...tableHeaderCellStyle, width: '70px' }}>
              子商品
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {menus.map((menu) => {
            const status = menuStatus(menu, businessDate);
            const price = displayPrice(menu, businessDate);
            const expandable = hasChildren(menu);
            const isOpen = expandable && expanded.has(menu.mets_menu_cd);
            const firstWebMenuCode = menu.web_menu_cds[0]?.web_menu_cd;
            const additionalWebMenuCodes = Math.max(
              0,
              menu.web_menu_cds.length - 1
            );
            const specialOrderCount = menu.web_menu_cds.reduce(
              (count, webMenu) =>
                count + webMenu.special_order_item_codes.length,
              0
            );
            return (
              <React.Fragment key={menu.mets_menu_cd}>
                <Tr
                  data-testid={`menu-row-${menu.mets_menu_cd}`}
                  onClick={() =>
                    expandable ? toggle(menu.mets_menu_cd) : undefined
                  }
                  cursor={expandable ? 'pointer' : 'default'}
                >
                  <Td color={expandable ? 'gray.600' : 'gray.300'}>
                    {isOpen ? '▾' : '▸'}
                  </Td>
                  <Td>{menu.mets_menu_cd}</Td>
                  <Td data-testid={`menu-web-codes-${menu.mets_menu_cd}`}>
                    {!firstWebMenuCode ? (
                      <Text as="span" color="gray.400">
                        —
                      </Text>
                    ) : (
                      // 補足はコード幅に収まらないので、モックどおり縦に積みます。
                      <Box
                        data-testid={`web-codes-stack-${menu.mets_menu_cd}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                        }}
                      >
                        <Text as="span" fontFamily="mono">
                          {firstWebMenuCode}
                        </Text>
                        {additionalWebMenuCodes > 0 && (
                          <Text as="span" color="gray.500" fontSize="11px">
                            他 {additionalWebMenuCodes} 件
                          </Text>
                        )}
                        {specialOrderCount > 0 && (
                          <Text as="span" color="gray.500" fontSize="11px">
                            特殊注文 {specialOrderCount} 件
                          </Text>
                        )}
                      </Box>
                    )}
                  </Td>
                  <Td>{menu.mets_screen_menu_name}</Td>
                  <Td data-testid={`menu-category-${menu.mets_menu_cd}`}>
                    <Badge
                      borderWidth="1px"
                      style={tableBadgeStyle}
                      {...categoryBadgeStyle(menuCategory(menu))}
                    >
                      {categoryLabel(menuCategory(menu))}
                    </Badge>
                  </Td>
                  <Td isNumeric>{formatYen(price?.sell_price)}</Td>
                  <Td data-testid={`menu-period-${menu.mets_menu_cd}`}>
                    {salesPeriod(menu)}
                  </Td>
                  <Td>
                    <Badge style={statusBadgeStyle(status)}>
                      {menuStatusLabel(status)}
                    </Badge>
                  </Td>
                  <Td isNumeric color={expandable ? undefined : 'gray.400'}>
                    {menu.source_relation_count}
                  </Td>
                </Tr>
                {isOpen && (
                  <Tr data-testid={`menu-detail-${menu.mets_menu_cd}`}>
                    <Td colSpan={9}>
                      <MenuRelationDetail
                        menu={menu}
                        businessDate={businessDate}
                      />
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
